import { useState } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SubscriptionAuditPanel({ getAuthHeader, onAfterFix }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tolerance, setTolerance] = useState(14);
  const [report, setReport] = useState(null);
  const [fixingId, setFixingId] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setReport(null);
    try {
      const res = await axios.get(
        `${API}/admin/audit/subscription-dates?tolerance_days=${tolerance}`,
        { headers: getAuthHeader() }
      );
      setReport(res.data);
    } catch (err) {
      alert(err.response?.data?.detail || 'Audit failed');
    } finally {
      setLoading(false);
    }
  };

  const reconcile = async () => {
    if (!window.confirm(
      "Scan Razorpay for membership payments stuck in 'pending' (older than 15 minutes) " +
      "and auto-complete any that Razorpay actually captured?\n\n" +
      "Use this when a member says they paid but the subscription didn't extend " +
      "(usually because they closed the Razorpay popup right after paying)."
    )) return;
    setReconciling(true);
    setReconcileResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/payments/reconcile-pending?older_than_minutes=15`,
        {},
        { headers: getAuthHeader() }
      );
      setReconcileResult(res.data);
      // Re-run audit so freshly-repaired members move out of "no payments" / "short credited"
      await run();
      if (onAfterFix) onAfterFix();
    } catch (err) {
      alert(err.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  const fixOne = async (entry) => {
    if (!window.confirm(`Recalculate ${entry.name}'s end date?\n\nOld: ${entry.actual_end}\nNew (from ${entry.n_payments} payment${entry.n_payments === 1 ? '' : 's'}, ${entry.total_paid_days} days): ${entry.expected_end}`)) return;
    setFixingId(entry.user_id);
    try {
      const res = await axios.post(
        `${API}/admin/audit/subscription-dates/fix/${entry.user_id}`,
        {},
        { headers: getAuthHeader() }
      );
      alert(`Fixed. New end: ${new Date(res.data.new_end).toLocaleDateString()}`);
      await run();
      if (onAfterFix) onAfterFix();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to fix');
    } finally {
      setFixingId(null);
    }
  };

  if (!open) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-6" data-testid="sub-audit-panel">
        <h3 className="font-semibold text-amber-900 mb-2">Subscription Date Audit</h3>
        <p className="text-sm text-amber-800 mb-3">
          Scan all members for `subscription_end_date` values that exceed what their successful
          payment history justifies — i.e., users affected by the verify-stacking bug fixed Feb 2026.
        </p>
        <button
          onClick={() => { setOpen(true); run(); }}
          className="px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-sm font-semibold"
          data-testid="open-sub-audit-btn"
        >Run audit</button>
      </div>
    );
  }

  return (
    <div className="bg-white border border-amber-300 rounded-xl shadow-lg p-6" data-testid="sub-audit-results">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-xl font-bold text-gray-800">Subscription Date Audit</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-gray-600">Tolerance (days)</label>
          <input
            type="number" min="1" max="365" value={tolerance}
            onChange={(e) => setTolerance(Math.max(1, Math.min(365, parseInt(e.target.value || '14', 10))))}
            className="w-20 border rounded px-2 py-1 text-sm"
            data-testid="audit-tolerance-input"
          />
          <button onClick={run} disabled={loading} className="px-3 py-1 bg-amber-600 text-white rounded text-sm font-semibold disabled:opacity-50" data-testid="audit-rerun-btn">
            {loading ? 'Scanning…' : 'Re-run'}
          </button>
          <button
            onClick={reconcile}
            disabled={reconciling}
            className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded text-sm font-semibold disabled:opacity-50"
            data-testid="reconcile-pending-btn"
            title="Poll Razorpay for stuck pending payments and complete them"
          >
            {reconciling ? 'Reconciling…' : 'Reconcile pending'}
          </button>
          <button onClick={() => setOpen(false)} className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm">Close</button>
        </div>
      </div>

      {reconcileResult && (
        <div className="mb-4 p-3 rounded-lg border border-blue-200 bg-blue-50" data-testid="reconcile-result">
          <div className="text-sm font-semibold text-blue-800 mb-1">
            Reconcile result: scanned {reconcileResult.scanned} · repaired {reconcileResult.repaired} · skipped {reconcileResult.skipped}{reconcileResult.errored ? ` · errored ${reconcileResult.errored}` : ''}
          </div>
          {reconcileResult.repaired > 0 && (
            <ul className="text-xs text-blue-900 space-y-1 max-h-40 overflow-y-auto mt-1">
              {reconcileResult.results.filter(r => r.outcome === 'repaired').map(r => (
                <li key={r.razorpay_order_id} className="border-t border-blue-200 py-1">
                  <span className="font-medium">{r.user_name}</span> ({r.user_email}) — {r.scenario} → end {new Date(r.new_end).toLocaleDateString()}{r.amount_paise ? ` · ₹${(r.amount_paise/100).toFixed(0)}` : ''}
                </li>
              ))}
            </ul>
          )}
          {reconcileResult.errored > 0 && (
            <details className="mt-2 text-xs text-red-700">
              <summary className="cursor-pointer">Show errors ({reconcileResult.errored})</summary>
              <ul className="space-y-1 mt-1 max-h-32 overflow-y-auto">
                {reconcileResult.results.filter(r => r.outcome === 'error').map(r => (
                  <li key={r.razorpay_order_id}>{r.razorpay_order_id}: {r.reason}</li>
                ))}
              </ul>
            </details>
          )}
        </div>
      )}

      {loading && <p className="text-gray-600">Scanning…</p>}

      {report && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-6 gap-3 mb-5">
            <Stat label="Audited" value={report.totals.members_audited} />
            <Stat label="Healthy" value={report.totals.healthy} color="green" />
            <Stat label="Stacked (fixable)" value={report.totals.suspect_stacked} color="red" />
            <Stat label="Short credited" value={report.totals.short_credited ?? 0} color="orange" />
            <Stat label="Autopay (review)" value={report.totals.autopay_needs_review} color="amber" />
            <Stat label="No payments" value={report.totals.no_payment_history} color="gray" />
          </div>

          <Section title={`Stacked dates — auto-fixable (${report.suspect.length})`} testId="suspect-section">
            {report.suspect.length === 0 ? (
              <p className="text-gray-500 text-sm">No stacked subscriptions detected. 🎉</p>
            ) : (
              <Table rows={report.suspect} fixingId={fixingId} onFix={fixOne} fixable variant="stacked" />
            )}
          </Section>

          <Section title={`Short-credited dates — auto-fixable (${(report.short_credited || []).length})`} testId="short-credited-section">
            {(report.short_credited || []).length === 0 ? (
              <p className="text-gray-500 text-sm">No short-credited members detected. 🎉</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2">
                  These members paid in advance but their <code>subscription_end_date</code> hasn&apos;t
                  been pushed forward to match. Usually means a verify-payment call partially failed
                  or took the &quot;start fresh&quot; branch instead of extending. Recalculating
                  rebuilds the end date as <code>first_payment + Σ duration_days</code>.
                </p>
                <Table rows={report.short_credited} fixingId={fixingId} onFix={fixOne} fixable variant="short" />
              </>
            )}
          </Section>

          <Section title={`Autopay — needs manual review (${report.needs_review.length})`} testId="autopay-section">
            {report.needs_review.length === 0 ? (
              <p className="text-gray-500 text-sm">No autopay anomalies.</p>
            ) : (
              <>
                <p className="text-xs text-gray-500 mb-2">
                  These users have autopay subscriptions. Their legacy renewals (before the Feb 2026 webhook fix) may not have payment rows, so the script can&apos;t compute the expected date reliably. Cross-check against the Razorpay dashboard, then use the per-user &quot;Adjust Subscription&quot; button.
                </p>
                <Table rows={report.needs_review} fixingId={null} onFix={null} fixable={false} />
              </>
            )}
          </Section>

          <Section title={`No payment history (${report.totals.no_payment_history})`} testId="no-pay-section">
            <p className="text-xs text-gray-500 mb-2">
              Likely admin-granted or pre-migration accounts. Usually fine — review only if you spot suspicious end dates.
            </p>
            {report.no_payment_history.length > 0 && (
              <ul className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                {report.no_payment_history.slice(0, 30).map((u) => (
                  <li key={u.user_id} className="border-b py-1">
                    {u.name} <span className="text-gray-400">({u.email})</span> — end: {u.actual_end}{u.has_autopay ? ' · autopay' : ''}
                  </li>
                ))}
              </ul>
            )}
          </Section>
        </>
      )}
    </div>
  );
}

function Stat({ label, value, color = 'blue' }) {
  const tones = {
    blue: 'bg-blue-50 text-blue-700 border-blue-200',
    green: 'bg-green-50 text-green-700 border-green-200',
    red: 'bg-red-50 text-red-700 border-red-200',
    orange: 'bg-orange-50 text-orange-700 border-orange-200',
    amber: 'bg-amber-50 text-amber-700 border-amber-200',
    gray: 'bg-gray-50 text-gray-700 border-gray-200',
  };
  return (
    <div className={`rounded-lg border p-3 ${tones[color] || tones.blue}`}>
      <p className="text-xs uppercase tracking-wide opacity-80">{label}</p>
      <p className="text-2xl font-bold">{value}</p>
    </div>
  );
}

function Section({ title, testId, children }) {
  return (
    <div className="mb-5" data-testid={testId}>
      <h4 className="font-semibold text-gray-800 mb-2">{title}</h4>
      {children}
    </div>
  );
}

function Table({ rows, fixingId, onFix, fixable, variant = 'stacked' }) {
  const badgeClass = variant === 'short'
    ? 'bg-orange-100 text-orange-700'
    : 'bg-red-100 text-red-700';
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
            <th className="py-2 px-3">Member</th>
            <th className="py-2 px-3">Actual end</th>
            <th className="py-2 px-3">Expected</th>
            <th className="py-2 px-3 text-right">Diff</th>
            <th className="py-2 px-3 text-right">Payments</th>
            {fixable && <th className="py-2 px-3 text-right">Action</th>}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.user_id} className="border-t hover:bg-gray-50" data-testid={`audit-row-${r.user_id}`}>
              <td className="py-2 px-3">
                <p className="font-medium text-gray-800">{r.name}</p>
                <p className="text-xs text-gray-500">{r.email}</p>
              </td>
              <td className="py-2 px-3 text-gray-700">{r.actual_end}</td>
              <td className="py-2 px-3 text-gray-700">{r.expected_end}</td>
              <td className="py-2 px-3 text-right">
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${badgeClass}`}>
                  {r.excess_days > 0 ? `+${r.excess_days}` : r.excess_days} d
                </span>
              </td>
              <td className="py-2 px-3 text-right text-gray-600">{r.n_payments} · {r.total_paid_days} d</td>
              {fixable && (
                <td className="py-2 px-3 text-right">
                  <button
                    onClick={() => onFix(r)}
                    disabled={fixingId === r.user_id}
                    className="px-3 py-1 bg-green-600 hover:bg-green-700 text-white rounded text-xs font-semibold disabled:opacity-50"
                    data-testid={`fix-audit-${r.user_id}`}
                  >{fixingId === r.user_id ? '…' : 'Recalculate'}</button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
